'use client';

import { useCallback, useEffect, useState } from 'react';
import { AdminDataTable } from '@/platform/admin/AdminDataTable';
import {
  approveCalendarEventRequest,
  fetchPendingCalendarEventRequests,
  rejectCalendarEventRequest,
} from './api';
import { formatEventWhen } from './eventUtils';
import type { CalendarEventRequest } from './types';

type Props = {
  onChanged?: () => void;
};

export function CalendarEventRequestsPanel({ onChanged }: Props) {
  const [requests, setRequests] = useState<CalendarEventRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchPendingCalendarEventRequests()
      .then((res) => setRequests(res.requests ?? []))
      .catch((err: Error) => {
        setRequests([]);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function approve(id: string) {
    try {
      await approveCalendarEventRequest(id);
      load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    }
  }

  async function reject(id: string) {
    const reason = window.prompt('Rejection reason (optional):') ?? '';
    try {
      await rejectCalendarEventRequest(id, reason);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed');
    }
  }

  return (
    <section className="calendar-requests">
      <h3>Submissions pending review</h3>
      <p className="text-muted">Community event submissions appear here until approved onto the calendar.</p>
      <AdminDataTable
        columns={[
          { key: 'title', header: 'Title', render: (row) => row.title },
          {
            key: 'when',
            header: 'When',
            render: (row) =>
              formatEventWhen({
                id: row.id,
                title: row.title,
                startAt: row.startAt,
                endAt: row.endAt,
                allDay: row.allDay,
              }),
          },
          { key: 'by', header: 'Submitted by', render: (row) => row.requestedBy },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <div className="admin-table__actions">
                <button type="button" className="btn btn-sm btn-primary" onClick={() => void approve(row.id)}>
                  Approve
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => void reject(row.id)}>
                  Reject
                </button>
              </div>
            ),
          },
        ]}
        rows={requests}
        rowKey={(row) => row.id}
        loading={loading}
        error={error}
        emptyMessage="No pending event submissions."
      />
    </section>
  );
}
